import {
  Archive,
  ChevronDown,
  ChevronRight,
  Lightbulb,
  ListPlus,
  Merge,
  MessageCircleQuestion,
  Trash2,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Chip } from "@/components/chip";
import {
  NARROW_ONLY,
  TABLE_ICON_CLASS,
  TableCell,
  TableGroupRow,
  TableItem,
  TableRow,
} from "@/components/data-table";
import { Highlight } from "@/components/highlight";
import { type RowActionItem, RowActions } from "@/components/row-action";
import type { RoadmapTriageEntry } from "@/lib/api";
import type { ChipTone } from "@/lib/chips";
import { formatDateTime, formatList } from "@/lib/format";
import { useLanguage } from "@/lib/i18n/language";
import type { NamedPerson } from "@/lib/people";
import { cn } from "@/lib/utils";

import {
  questionReasonLabel,
  submitterLabel,
  type TriageAction,
  triageKind,
  triedToolTitle,
} from "./triage";
import { PRIORITY_TONE, priorityLabel } from "./utils";

/**
 * An entry's row spans the item's column up to the month's, so it reads
 * across the table, and leaves the last three columns to its four actions:
 * the deadline's, the followers' and the follow button's. That is room enough
 * for them in the wide layout, so they never widen a column the items set.
 * Below 900 px of the table's box the month and the deadline are out, and the
 * actions lose their padding to fit the other two (`ACTIONS_CELL`).
 */
const ENTRY_SPAN = 4;
const ACTIONS_SPAN = 3;
const ACTIONS_CELL = "px-1 py-1 @max-[900px]:px-0";

/** A reason's chip: a missing tool, which Velta can build, in De Ceuster's light blue; the rest in grey. */
const REASON_TONE: Record<string, ChipTone> = {
  missing_tool: "read",
};

interface TriageGroupProps {
  /** The entries the table shows, newest first. */
  entries: readonly RoadmapTriageEntry[];
  /** The table's number of columns. */
  colSpan: number;
  expanded: boolean;
  onToggle: () => void;
  /** The people the triage names its submitters among (`triagePeople`), by email in lower case. */
  people: ReadonlyMap<string, NamedPerson>;
  /** The search as typed, marked in the entries. */
  search: string;
  /**
   * An action on an entry: Naar backlog opens the idea's dialog for it;
   * Samenvoegen met…, Sluiten and Verwijderen each ask first in a dialog.
   */
  onAction: (action: TriageAction, entry: RoadmapTriageEntry) => void;
  /** An action starts: an earlier failure from a row no longer holds. */
  onClearError: () => void;
}

/**
 * Triage, for a developer only: the ideas others proposed and the questions
 * DC Data could not answer, in a group of their own that folds like the
 * others, with their count on the right. Its header is solid De Ceuster blue
 * with white text, so it stands out from the light headers of the others.
 */
export function TriageGroup({ entries, colSpan, expanded, onToggle, ...row }: TriageGroupProps) {
  const { t } = useTranslation();
  return (
    <tbody data-group="triage">
      <TableGroupRow
        tone="brand"
        title={t("roadmap.triage.title")}
        colSpan={colSpan}
        collapse={{ expanded, onToggle }}
        aside={t("roadmap.itemCount", { count: entries.length })}
      />
      {expanded && entries.map((entry) => <TriageRow key={entry.id} entry={entry} {...row} />)}
    </tbody>
  );
}

type TriageRowProps = Omit<TriageGroupProps, "entries" | "colSpan" | "expanded" | "onToggle"> & {
  entry: RoadmapTriageEntry;
};

/**
 * One entry: its title, who submitted it and when, its kind, priority and,
 * for a question, why Claude could not answer as chips, and under Details
 * its text and, for a question, the tools Claude tried and its note. Its
 * actions, each in a dialog: Naar backlog, Samenvoegen met… (into an item
 * already on the roadmap), Sluiten and, in red, Verwijderen. In the phone
 * layout the actions sit in a menu at the row's right.
 */
function TriageRow({ entry, people, search, onAction, onClearError }: TriageRowProps) {
  const { t } = useTranslation();
  const language = useLanguage();
  const [open, setOpen] = useState(false);
  const kind = triageKind(entry);
  const question = kind === "question";
  const person = submitterLabel(entry, people);
  // A question's title is the question itself, cut: its text adds something only when it is longer.
  const bodyShown = entry.body.trim() !== entry.title.trim();
  const hasDetails = bodyShown || question;

  const act = (action: TriageAction) => () => {
    onClearError();
    onAction(action, entry);
  };
  const actions: RowActionItem[] = [
    {
      label: t(`roadmap.triage.acceptLabel.${kind}`, { person }),
      short: t("roadmap.triage.accept"),
      icon: ListPlus,
      onClick: act("accept"),
    },
    {
      label: t(`roadmap.triage.mergeLabel.${kind}`, { person }),
      short: t("roadmap.triage.merge"),
      icon: Merge,
      onClick: act("merge"),
    },
    {
      label: t(`roadmap.triage.closeLabel.${kind}`, { person }),
      short: t("roadmap.triage.close"),
      icon: Archive,
      onClick: act("close"),
    },
    {
      label: t(`roadmap.triage.deleteLabel.${kind}`, { person }),
      short: t("roadmap.triage.delete"),
      icon: Trash2,
      destructive: true,
      onClick: act("delete"),
    },
  ];
  const actionsLabel = t(`roadmap.triage.actions.${kind}`, { person });

  return (
    <TableRow>
      <TableCell colSpan={ENTRY_SPAN}>
        {/* No width of its own, so a long entry never changes the columns the items set. */}
        <div className="flex w-0 min-w-full items-start gap-2" data-triage={entry.id}>
          <div className="min-w-0 flex-1">
            <TableItem
              icon={
                question ? (
                  <MessageCircleQuestion aria-hidden="true" className={TABLE_ICON_CLASS} />
                ) : (
                  <Lightbulb aria-hidden="true" className={TABLE_ICON_CLASS} />
                )
              }
              title={
                // Cut at three lines only while Details can show the rest; a title alone shows in full.
                <span className={cn(hasDetails && !open && "line-clamp-3")}>
                  <Highlight text={entry.title} query={search} />
                </span>
              }
              summary={t("roadmap.triage.submittedBy", {
                person,
                date: formatDateTime(entry.created_at),
              })}
            >
              <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1">
                <span className="flex flex-wrap items-center gap-1.5">
                  <Chip tone="neutral">{t(`roadmap.triage.kinds.${kind}`)}</Chip>
                  {entry.priority && (
                    <Chip tone={PRIORITY_TONE[entry.priority] ?? "neutral"}>
                      {priorityLabel(entry.priority)}
                    </Chip>
                  )}
                  {question && entry.reason && (
                    <Chip tone={REASON_TONE[entry.reason] ?? "neutral"}>
                      {questionReasonLabel(entry.reason)}
                    </Chip>
                  )}
                </span>
                {hasDetails && (
                  <button
                    type="button"
                    aria-expanded={open}
                    onClick={() => setOpen(!open)}
                    className="text-muted hover:text-primary focus-visible:ring-ring tap-target inline-flex items-center gap-1 rounded-sm text-xs font-semibold focus-visible:ring-2 focus-visible:outline-none pointer-coarse:relative"
                  >
                    {open ? (
                      <ChevronDown aria-hidden="true" className="size-3.5" />
                    ) : (
                      <ChevronRight aria-hidden="true" className="size-3.5" />
                    )}
                    {t("roadmap.triage.showDetails")}
                  </button>
                )}
              </div>
              {open && (
                // Label and value side by side, or under each other in the phone layout.
                <dl className="mt-1.5 grid grid-cols-1 gap-x-3 gap-y-0.5 text-xs @min-[640px]:grid-cols-[auto_minmax(0,1fr)]">
                  {bodyShown && (
                    <>
                      <dt className="text-muted">{t(`roadmap.triage.body.${kind}`)}</dt>
                      <dd className="text-foreground wrap-anywhere whitespace-pre-wrap @max-[640px]:mb-1">
                        <Highlight text={entry.body} query={search} />
                      </dd>
                    </>
                  )}
                  {question && (
                    <>
                      <dt className="text-muted">{t("roadmap.triage.toolsTried")}</dt>
                      <dd className="text-foreground @max-[640px]:mb-1">
                        {entry.tools_tried.length > 0
                          ? formatList(
                              entry.tools_tried.map((name) => triedToolTitle(name, language)),
                            )
                          : t("roadmap.triage.noTools")}
                      </dd>
                    </>
                  )}
                  {question && entry.detail && (
                    <>
                      <dt className="text-muted">{t("roadmap.triage.detail")}</dt>
                      <dd className="text-foreground wrap-anywhere whitespace-pre-wrap">
                        <Highlight text={entry.detail} query={search} />
                      </dd>
                    </>
                  )}
                </dl>
              )}
            </TableItem>
          </div>
          {/* The phone layout's actions: a menu at the row's right, as the actions' column left. */}
          <div className={NARROW_ONLY}>
            <RowActions label={actionsLabel} actions={actions} />
          </div>
        </div>
      </TableCell>
      <TableCell colSpan={ACTIONS_SPAN} narrow="empty" align="right" className={ACTIONS_CELL}>
        <RowActions label={actionsLabel} actions={actions} />
      </TableCell>
    </TableRow>
  );
}
